import React from 'react';

import { kebabCase } from 'lodash';
import styled from 'styled-components';
import { prop } from 'styled-tools';

import ColorableButton from 'components/ColorableButton/ColorableButton';
import { FooterData } from 'components/Layout/Footer/FooterData';
import LegalSection from 'components/Layout/Footer/components/LegalSection';
import MenuSection from 'components/Layout/Footer/components/MenuSection';

import { pushAddAccountEvent, pushLoginEvent } from 'utils/dataLayerUtils';
import { getModularBlockLinkData } from 'utils/modularBlockUtils';

const StyledFooter = styled.footer`
  background-color: ${(props) => props.theme.colors.primaryBlack2};
  padding-top: 5rem;
  padding-bottom: 6rem;
  position: relative;
  z-index: 2;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;

  p {
    font-family: ${prop('theme.fonts.default')};
    font-size: 0.6875rem;
    color: ${(props) => props.theme.colors.transparentGray};
    line-height: 1.5em;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: 3rem 0;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    padding: 2.5rem 0;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    padding: 2rem 0;
  }
`;

const FooterContentContainer = styled.div`
  width: 100%;
  padding-left: 5rem;
  padding-right: 5rem;
  position: relative;

  @media (min-width: 90rem) {
    max-width: 1340px;
    margin-left: auto;
    margin-right: auto;
    padding: 0;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    padding-left: 3rem;
    padding-right: 3rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;

const TopRowContainer = styled.div`
  display: grid;
  grid-auto-columns: 1fr;
  grid-template-columns: 1fr 1fr 0.5fr auto;
  grid-template-rows: auto;
  grid-column-gap: 2rem;
  grid-row-gap: 2rem;
  grid-column: span 1 / span 1;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    grid-column-gap: 1rem;
    grid-row-gap: 1rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-row-gap: 2rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    grid-template-columns: 1fr;
  }

  a:last-child {
    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
      grid-area: span 1 / span 2 / span 1 / span 2;
    }

    @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
      grid-area: span 1 / span 1;
    }
  }
`;

const LogoImage = styled.img`
  grid-area: span 1 / span 2 / span 1 / span 2;

  width: 8rem;
  max-width: 100%;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    grid-column: span 1 / span 1;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    margin-bottom: 0.5rem;
  }
`;

interface FooterProps {
  data: FooterData;
  hideFooterMenu?: boolean;
}

const Footer: React.FC<FooterProps> = ({ data, hideFooterMenu = false }) => {
  const handleButtonClick = (text: string) => {
    pushAddAccountEvent(text, { interaction_type: 'click', interaction_value: text });
    pushLoginEvent(text, 'click footer');
  };

  return (
    <StyledFooter data-testId={'footer'}>
      <FooterContentContainer>
        <TopRowContainer>
          <LogoImage alt='tastyfx logo' className='logo' src={data.logo?.url} data-testId={'footer-logo'} />
          {data.buttonCta.map(({ buttonCta }, idx) => {
            const { link, target } = getModularBlockLinkData(buttonCta.buttonLink);
            return (
              <ColorableButton
                data-testId={`footer-link-${kebabCase(buttonCta.buttonText)}`}
                key={idx}
                to={link}
                target={target}
                $color={buttonCta.buttonColor}
                $justify={buttonCta.buttonPlacement !== 'center' ? buttonCta.buttonPlacement : null}
                onClick={() => handleButtonClick(buttonCta.buttonText)}
              >
                {buttonCta.buttonText}
              </ColorableButton>
            );
          })}
        </TopRowContainer>
        {!hideFooterMenu && data.menus?.length > 0 && <MenuSection data={data} />}
        <LegalSection data={data} />
      </FooterContentContainer>
    </StyledFooter>
  );
};

export default Footer;
